import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Save, Activity, FileText, Scissors, Receipt, XCircle, BookmarkPlus, Sparkles, Trash2, Plus, Pill, Check, Printer, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import MicDictateButton from "@/components/MicDictateButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Discharge medication is now a structured Rx table (mirrors OPD) — rendered separately, not via DISCHARGE_FIELDS.
const DISCHARGE_FIELDS = [
  { key: "final_diagnosis", label: "Final diagnosis", area: true },
  { key: "procedure_performed", label: "Procedure performed", area: false },
  { key: "clinical_summary", label: "Clinical summary", area: true },
  { key: "investigation_summary", label: "Investigation summary", area: true },
  { key: "treatment_given", label: "Treatment given", area: true },
  { key: "operative_details", label: "Operative details", area: true },
  { key: "condition_at_discharge", label: "Condition at discharge", area: false },
  { key: "follow_up_advice", label: "Follow-up advice", area: true, afterRx: true },
  { key: "emergency_instructions", label: "Emergency instructions", area: true, afterRx: true },
  { key: "red_flag_symptoms", label: "Red-flag symptoms", area: true, afterRx: true },
  { key: "diet_activity", label: "Diet / activity", area: false, afterRx: true },
  { key: "post_op_eye_care", label: "Post-op eye care", area: true, afterRx: true },
];

// Surgery / diagnosis-based template categories shared by IPD discharge & OT note
const SURGERY_CATEGORIES = [
  "Cataract / Phaco", "Cataract / SICS", "IOL Exchange",
  "Glaucoma / Trabeculectomy", "Glaucoma / Implant",
  "Vitreoretinal / Vitrectomy", "Vitreoretinal / Buckle",
  "Retina / Intravitreal Injection",
  "Cornea / Keratoplasty", "Cornea / Pterygium",
  "Oculoplasty / Lid Surgery", "Oculoplasty / DCR",
  "Squint / Strabismus",
  "Refractive / LASIK", "Refractive / PRK",
  "Other",
];

export default function IpdDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const EMPTY_NOTE = { bp: "", pulse: "", temperature: "", spo2: "", intake: "", output: "", medications_given: "", medications: [], notes: "", include_in_discharge: false };
  const [note, setNote] = useState(EMPTY_NOTE);
  const [discharge, setDischarge] = useState({});
  const [otBound, setOtBound] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [tplDialogOpen, setTplDialogOpen] = useState(false);
  const [tplName, setTplName] = useState("");
  const [tplDesc, setTplDesc] = useState("");
  const [tplCategory, setTplCategory] = useState("");
  const [tplFilterCategory, setTplFilterCategory] = useState("All");
  const [selectedTpl, setSelectedTpl] = useState("");
  const [invoices, setInvoices] = useState([]);          // bills tied to this admission, for the Billing & invoices tab

  const loadTemplates = async () => {
    try {
      const { data } = await api.get("/ipd/discharge-templates");
      setTemplates(data.items || []);
    } catch (_e) { /* non-blocking */ }
  };
  useEffect(() => { loadTemplates(); }, []);

  const applyTemplate = (tplId) => {
    setSelectedTpl(tplId);
    if (!tplId) return;
    const tpl = templates.find(t => t.id === tplId);
    if (!tpl) return;
    setDischarge(prev => ({ ...prev, ...(tpl.fields || {}) }));
    toast.success(`Template "${tpl.name}" applied`);
  };

  const saveAsTemplate = async () => {
    if (!tplName.trim()) { toast.error("Template name is required"); return; }
    // Strip iol_used (instance-specific) from saved template
    const { iol_used, ...rest } = discharge;
    void iol_used;
    try {
      await api.post("/ipd/discharge-templates", {
        name: tplName.trim(),
        description: tplDesc.trim(),
        category: tplCategory.trim(),
        fields: rest,
      });
      toast.success("Template saved");
      setTplDialogOpen(false); setTplName(""); setTplDesc(""); setTplCategory("");
      loadTemplates();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const deleteTemplate = async (tplId, name) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    try {
      await api.delete(`/ipd/discharge-templates/${tplId}`);
      toast.success("Template deleted");
      if (selectedTpl === tplId) setSelectedTpl("");
      loadTemplates();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const load = async () => {
    const { data } = await api.get(`/ipd/admissions/${id}`);
    setData(data);
    // Pull invoices linked to this admission for the "Billing & invoices" tab.
    try {
      const inv = await api.get("/billing/invoices", { params: { admission_id: id, limit: 200 } });
      setInvoices(inv.data.items || []);
    } catch { /* non-fatal — admission detail still renders */ }
    if (data.discharge) {
      setDischarge(data.discharge);
      // Already bound from a previous save?
      if (data.discharge.iol_used && (data.discharge.iol_used.implant_name || data.discharge.iol_used.model)) {
        setOtBound(true);
      }
    } else if (data.latest_ot) {
      // First-time open: pre-fill from the latest OT note (procedure + operative details + IOL)
      const ot = data.latest_ot;
      const iol = ot.iol || {};
      const opdets = [
        ot.eye_operated ? `Eye operated: ${ot.eye_operated}` : "",
        ot.intra_op_notes ? `Intra-op: ${ot.intra_op_notes}` : "",
        ot.post_op_notes ? `Post-op: ${ot.post_op_notes}` : "",
      ].filter(Boolean).join("\n");
      setDischarge({
        procedure_performed: ot.procedure_performed || "",
        operative_details: opdets,
        iol_used: {
          implant_id: iol.implant_id || "",
          implant_name: iol.implant_name || "",
          model: iol.model || "",
          power: iol.power || "",
          batch_no: iol.batch_no || "",
          lot_no: iol.lot_no || "",
          expiry: iol.expiry || "",
        },
      });
      setOtBound(true);
      toast.info("Auto-filled procedure & IOL details from the patient's latest OT note.", { duration: 4000 });
    }
  };
  useEffect(() => { load(); }, [id]);

  const addNote = async () => {
    try { await api.post(`/ipd/admissions/${id}/notes`, note); setNote(EMPTY_NOTE); toast.success("Nursing note saved"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  const toggleIncludeInDischarge = async (noteId, current) => {
    try {
      await api.patch(`/ipd/admissions/${id}/notes/${noteId}`, { include_in_discharge: !current });
      toast.success(!current ? "Will print in discharge" : "Removed from discharge print");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  // Structured medication rows on the nursing note form
  const noteMeds = note.medications || [];
  const addNoteMed = () => setNote({
    ...note,
    medications: [...noteMeds, { medicine: "", medicine_id: "", strength: "", dose: "", route: "Oral", time: "", notes: "" }],
  });
  const updateNoteMed = (i, k, v) => {
    const next = [...noteMeds];
    next[i] = { ...next[i], [k]: v };
    // Auto-bind medicine_id + strength when the typed name matches the master
    if (k === "medicine") {
      const match = medicines.find((m) => (m.name || "").toLowerCase() === (v || "").toLowerCase());
      if (match) {
        next[i].medicine_id = match.id;
        if (!next[i].strength && match.strength) next[i].strength = match.strength;
      } else {
        next[i].medicine_id = "";
      }
    }
    setNote({ ...note, medications: next });
  };
  const removeNoteMed = (i) => setNote({ ...note, medications: noteMeds.filter((_, j) => j !== i) });

  const saveDischarge = async () => {
    try { await api.post(`/ipd/admissions/${id}/discharge`, discharge); toast.success("Discharge summary saved · patient discharged"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  // -------- Discharge prescription (Rx table — mirrors OPD) --------
  const [medicines, setMedicines] = useState([]);
  useEffect(() => {
    api.get("/masters/medicines").then((r) => setMedicines(r.data.items || [])).catch(() => {});
  }, []);
  const stockOf = (m) => {
    if (m.medicine_id) {
      const x = medicines.find((y) => y.id === m.medicine_id);
      return x ? x.stock : null;
    }
    const x = medicines.find((y) => y.name.toLowerCase() === (m.medicine || "").toLowerCase());
    return x ? x.stock : null;
  };
  const rxRows = discharge.discharge_prescription || [];
  const addRx = () => setDischarge({
    ...discharge,
    discharge_prescription: [...rxRows, { medicine: "", medicine_id: "", generic_name: "", strength: "", dosage: "", route: "Topical", eye: "OU", frequency: "", timing: "", duration: "", instructions: "" }],
  });
  const updateRx = (i, k, v) => {
    const next = [...rxRows];
    next[i] = { ...next[i], [k]: v === "_none" ? "" : v };
    // Auto-link to medicine master when name matches → snapshot id + generic
    if (k === "medicine") {
      const found = medicines.find((m) => (m.name || "").toLowerCase() === (v || "").toLowerCase());
      if (found) {
        next[i].medicine_id = found.id;
        next[i].generic_name = found.generic || "";
      } else {
        next[i].medicine_id = "";
        next[i].generic_name = "";
      }
    }
    setDischarge({ ...discharge, discharge_prescription: next });
  };
  const removeRx = (i) => setDischarge({ ...discharge, discharge_prescription: rxRows.filter((_, idx) => idx !== i) });

  if (!data) return <div className="text-sm text-ink-muted">Loading…</div>;
  const a = data.admission;

  return (
    <div className="space-y-6 animate-fade-in">
      <Card className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="section-label">Admission {a.ipd_no && <span className="ml-2 font-mono text-[10px] text-brand">· {a.ipd_no}</span>}</div>
            <h1 className="font-heading text-2xl font-semibold">{a.patient_name} <span className="ml-2 text-sm font-body text-ink-muted">{a.patient_uhid}</span></h1>
            <div className="mt-1 text-sm text-ink-muted">Type: <b>{a.admission_type.replace("_"," ")}</b> · Ward: {a.ward || "—"} · Bed: {a.bed_no || "—"} · Admitted {(a.admitted_at||"").slice(0,16).replace("T"," ")} · Status: <b>{a.status}</b>{a.discharged_at ? <> · Discharged {a.discharged_at.slice(0,16).replace("T"," ")}</> : null}</div>
            {(a.payer_category && a.payer_category !== "cash") && (
              <div className="mt-2 inline-flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-brand/20 bg-brand/5 px-3 py-1.5 text-xs" data-testid="ipd-payer-banner">
                <span className="font-semibold uppercase tracking-wider text-brand">{a.payer_category}</span>
                {a.panel_name && <span><b>Panel:</b> {a.panel_name}</span>}
                {a.policy_no && <span><b>Policy no.:</b> <span className="font-mono">{a.policy_no}</span></span>}
                {a.tpa_name && <span><b>TPA:</b> {a.tpa_name}</span>}
                {a.corporate_name && <span><b>Corporate:</b> {a.corporate_name}</span>}
              </div>
            )}
          </div>
          <Link to={`/app/ipd/${id}/bill`} data-testid="ipd-detail-open-bill-link"><Button className="bg-brand hover:bg-brand-700" data-testid="ipd-open-bill-btn"><Receipt size={14} className="mr-2" />IPD Billing</Button></Link>
          {a.status !== "cancelled" && a.status !== "discharged" && (
            <Button variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50 ml-2" data-testid="cancel-admission-btn" onClick={async () => {
              const reason = window.prompt("Cancellation reason (required):");
              if (!reason || !reason.trim()) return;
              try {
                await api.post(`/ipd/admissions/${id}/cancel`, { reason: reason.trim() });
                toast.success("Admission cancelled");
                load();
              } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
            }}>
              <XCircle size={14} className="mr-2" />Cancel admission
            </Button>
          )}
          {a.status === "cancelled" && (
            <div className="ml-2 inline-flex items-center rounded-md bg-rose-100 px-3 py-1 text-xs font-semibold text-rose-800">
              Cancelled · {a.cancel_reason}
            </div>
          )}
        </div>
      </Card>

      <Tabs defaultValue="notes">
        <TabsList>
          <TabsTrigger value="notes" data-testid="ipd-tab-notes"><Activity size={14} className="mr-2" />Nursing notes</TabsTrigger>
          <TabsTrigger value="billing" data-testid="ipd-tab-billing"><Receipt size={14} className="mr-2" />Billing &amp; invoices</TabsTrigger>
          <TabsTrigger value="discharge" data-testid="ipd-tab-discharge"><FileText size={14} className="mr-2" />Discharge summary</TabsTrigger>
        </TabsList>

        <TabsContent value="notes" className="mt-4 space-y-4">
          <Card className="p-5">
            <div className="mb-3 flex items-center justify-between">
              <div className="section-label mb-0">Add vitals / nursing note</div>
              <a href={`/print/nursing-notes/${id}`} target="_blank" rel="noreferrer" data-testid="nn-print-all">
                <Button size="sm" variant="outline" title="Print every nursing entry on this admission (separate report)">
                  <FileText size={12} className="mr-1" />Print all nursing notes
                </Button>
              </a>
            </div>
            <div className="form-grid">
              <div><Label>BP</Label><Input value={note.bp} onChange={e => setNote({...note, bp: e.target.value})} placeholder="120/80" data-testid="nn-bp" /></div>
              <div><Label>Pulse</Label><Input value={note.pulse} onChange={e => setNote({...note, pulse: e.target.value})} placeholder="78" data-testid="nn-pulse" /></div>
              <div><Label>Temp</Label><Input value={note.temperature} onChange={e => setNote({...note, temperature: e.target.value})} placeholder="98.6 F" data-testid="nn-temp" /></div>
              <div><Label>SpO2</Label><Input value={note.spo2} onChange={e => setNote({...note, spo2: e.target.value})} placeholder="98%" data-testid="nn-spo2" /></div>
              <div><Label>Intake</Label><Input value={note.intake} onChange={e => setNote({...note, intake: e.target.value})} data-testid="nn-intake" /></div>
              <div><Label>Output</Label><Input value={note.output} onChange={e => setNote({...note, output: e.target.value})} data-testid="nn-output" /></div>
              <div className="md:col-span-3"><Label>Notes</Label><Textarea rows={2} value={note.notes} onChange={e => setNote({...note, notes: e.target.value})} data-testid="nn-notes" /></div>
            </div>

            {/* Structured medication rows — linked to medicines master */}
            <div className="mt-4 rounded-md border border-border bg-secondary/30 p-3">
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand">
                  <Pill size={13} /> Medications given
                </div>
                <Button size="sm" variant="outline" onClick={addNoteMed} data-testid="nn-add-med">
                  <Plus size={12} className="mr-1" />Add medicine
                </Button>
              </div>
              {noteMeds.length === 0 ? (
                <div className="py-2 text-center text-[11px] text-ink-muted">No medications added — click "Add medicine" to link from master.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase text-ink-muted">
                    <tr>
                      <th className="px-2 py-1 text-left">Medicine</th>
                      <th className="px-2 py-1 text-left">Strength</th>
                      <th className="px-2 py-1 text-left">Dose</th>
                      <th className="px-2 py-1 text-left">Route</th>
                      <th className="px-2 py-1 text-left">Time</th>
                      <th className="px-2 py-1 text-left">Notes</th>
                      <th className="px-2 py-1"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {noteMeds.map((m, i) => (
                      <tr key={i} className="border-t border-border">
                        <td className="px-2 py-1">
                          <Input
                            list={`nn-med-list-${i}`}
                            value={m.medicine}
                            onChange={(e) => updateNoteMed(i, "medicine", e.target.value)}
                            placeholder="Type / pick from master"
                            data-testid={`nn-med-name-${i}`}
                          />
                          <datalist id={`nn-med-list-${i}`}>
                            {medicines.map((mm) => <option key={mm.id} value={mm.name}>{mm.strength ? `${mm.strength}` : ""}{mm.stock !== undefined ? ` · stock ${mm.stock}` : ""}</option>)}
                          </datalist>
                        </td>
                        <td className="px-2 py-1"><Input value={m.strength} onChange={(e) => updateNoteMed(i, "strength", e.target.value)} placeholder="e.g. 500mg" /></td>
                        <td className="px-2 py-1"><Input value={m.dose} onChange={(e) => updateNoteMed(i, "dose", e.target.value)} placeholder="1 tab" /></td>
                        <td className="px-2 py-1">
                          <Select value={m.route || "Oral"} onValueChange={(v) => updateNoteMed(i, "route", v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {["Oral", "IV", "IM", "SC", "Topical", "Eye drops", "Inhaled", "PR", "PV", "Other"].map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </td>
                        <td className="px-2 py-1"><Input type="time" value={m.time} onChange={(e) => updateNoteMed(i, "time", e.target.value)} /></td>
                        <td className="px-2 py-1"><Input value={m.notes} onChange={(e) => updateNoteMed(i, "notes", e.target.value)} placeholder="—" /></td>
                        <td className="px-2 py-1 text-right"><Button size="sm" variant="ghost" onClick={() => removeNoteMed(i)}><Trash2 size={12} /></Button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            <div className="mt-3 flex items-center justify-between">
              <label className="inline-flex cursor-pointer items-center gap-2 text-xs text-ink" data-testid="nn-include-toggle">
                <input
                  type="checkbox"
                  className="h-4 w-4"
                  checked={!!note.include_in_discharge}
                  onChange={(e) => setNote({ ...note, include_in_discharge: e.target.checked })}
                />
                Include this entry in printed discharge summary
              </label>
              <Button onClick={addNote} className="bg-brand hover:bg-brand-700" data-testid="ipd-add-note">
                <Save size={14} className="mr-2" />Add note
              </Button>
            </div>
          </Card>

          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary text-xs uppercase tracking-wider text-ink-muted">
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-left">By</th>
                  <th className="px-3 py-2 text-left">BP</th>
                  <th className="px-3 py-2 text-left">Pulse</th>
                  <th className="px-3 py-2 text-left">Temp</th>
                  <th className="px-3 py-2 text-left">SpO2</th>
                  <th className="px-3 py-2 text-left">Medications</th>
                  <th className="px-3 py-2 text-left">Notes</th>
                  <th className="px-3 py-2 text-center" title="Tick to print this nursing note inside the discharge summary">In discharge</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {data.nursing_notes?.length === 0 && <tr><td colSpan="9" className="px-3 py-10 text-center text-ink-muted">No notes yet.</td></tr>}
                {data.nursing_notes?.map((n) => {
                  const meds = Array.isArray(n.medications) && n.medications.length > 0
                    ? n.medications.map((m) => [m.medicine, m.strength, m.dose, m.route, m.time].filter(Boolean).join(" · ")).filter(Boolean).join("; ")
                    : (n.medications_given || "");
                  return (
                    <tr key={n.id}>
                      <td className="px-3 py-2 text-xs">{(n.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="px-3 py-2 text-xs">{n.created_by_name}</td>
                      <td className="px-3 py-2">{n.bp}</td>
                      <td className="px-3 py-2">{n.pulse}</td>
                      <td className="px-3 py-2">{n.temperature}</td>
                      <td className="px-3 py-2">{n.spo2}</td>
                      <td className="px-3 py-2 text-xs">{meds || "—"}</td>
                      <td className="px-3 py-2">{n.notes}</td>
                      <td className="px-3 py-2 text-center">
                        <button
                          onClick={() => toggleIncludeInDischarge(n.id, !!n.include_in_discharge)}
                          title={n.include_in_discharge ? "Will print in discharge — click to remove" : "Click to include in discharge print"}
                          className={`inline-flex h-6 w-6 items-center justify-center rounded border transition ${n.include_in_discharge ? "border-brand bg-brand text-white" : "border-border bg-white text-ink-muted hover:border-brand/40 hover:text-brand"}`}
                          data-testid={`nn-include-row-${n.id}`}
                        >
                          {n.include_in_discharge ? <Check size={14} /> : ""}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {/* Bottom action bar — easier to reach after a long list of notes */}
            {data.nursing_notes && data.nursing_notes.length > 0 && (
              <div className="flex items-center justify-between border-t border-border bg-secondary/20 px-3 py-2.5">
                <div className="text-[11px] text-ink-muted">
                  {data.nursing_notes.length} entries ·{" "}
                  {data.nursing_notes.filter((n) => n.include_in_discharge).length} ticked for discharge
                </div>
                <a href={`/print/nursing-notes/${id}`} target="_blank" rel="noreferrer" data-testid="nn-print-all-bottom">
                  <Button className="bg-brand hover:bg-brand-700" size="sm" title="Print every nursing entry on this admission (separate colour-coded report)">
                    <FileText size={12} className="mr-1" /> Print all nursing notes
                  </Button>
                </a>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="billing" className="mt-4">
          <Card className="p-5" data-testid="ipd-billing-tab">
            <div className="mb-3 flex items-center justify-between gap-2">
              <div>
                <div className="section-label mb-0">IPD Billing &amp; Invoices</div>
                <div className="mt-1 text-[11px] text-ink-muted">All invoices generated against this admission — including running, finalised, settled and refunded bills.</div>
              </div>
              <Link to={`/app/ipd/${id}/bill`} data-testid="ipd-billing-tab-open-full">
                <Button size="sm" className="bg-brand hover:bg-brand-700"><Receipt size={14} className="mr-2" />Open IPD Billing</Button>
              </Link>
            </div>

            {invoices.length === 0 ? (
              <div className="rounded-md border border-dashed border-border bg-secondary/30 p-6 text-center text-xs text-ink-muted">
                No invoices yet for this admission. Click <b>Open IPD Billing</b> to start a bill.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary text-[10px] uppercase tracking-wider text-ink-muted">
                    <tr>
                      <th className="px-3 py-2 text-left">Receipt no.</th>
                      <th className="px-3 py-2 text-left">Type</th>
                      <th className="px-3 py-2 text-left">Date</th>
                      <th className="px-3 py-2 text-right">Total</th>
                      <th className="px-3 py-2 text-right">Paid</th>
                      <th className="px-3 py-2 text-right">Balance</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {invoices.map((inv) => {
                      const statusTone = {
                        paid: "border-emerald-300 bg-emerald-50 text-emerald-800",
                        partial: "border-amber-300 bg-amber-50 text-amber-800",
                        due: "border-rose-300 bg-rose-50 text-rose-800",
                        pending_settlement: "border-sky-300 bg-sky-50 text-sky-800",
                        partial_settlement: "border-sky-300 bg-sky-50 text-sky-800",
                        refunded: "border-rose-300 bg-rose-50 text-rose-800",
                        cancelled: "border-ink-muted/30 bg-secondary text-ink-muted",
                        free: "border-emerald-300 bg-emerald-50 text-emerald-800",
                      }[inv.status] || "";
                      return (
                        <tr key={inv.id} data-testid={`ipd-billing-row-${inv.id}`}>
                          <td className="px-3 py-2 font-mono text-xs">{inv.invoice_no}</td>
                          <td className="px-3 py-2 text-xs">{inv.receipt_type || inv.bill_type || "—"}</td>
                          <td className="px-3 py-2 text-xs text-ink-muted">{(inv.created_at || "").slice(0, 16).replace("T", " ")}</td>
                          <td className="px-3 py-2 text-right font-semibold">₹ {Number(inv.total || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                          <td className="px-3 py-2 text-right text-leaf">₹ {Number(inv.amount_paid || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                          <td className={`px-3 py-2 text-right ${Number(inv.balance || 0) > 0.01 ? "text-rose-700 font-semibold" : "text-ink-muted"}`}>
                            ₹ {Number(inv.balance || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-3 py-2 text-xs">
                            <span className={`inline-flex rounded border px-1.5 py-0.5 text-[10px] font-semibold uppercase ${statusTone}`}>
                              {(inv.status || "").replace("_", " ")}
                            </span>
                            {inv.bill_state === "final" && (
                              <span className="ml-1 inline-flex rounded border border-emerald-300 bg-emerald-50 px-1 py-0.5 text-[9px] font-semibold uppercase text-emerald-700">Final</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right">
                            <div className="flex justify-end gap-1">
                              <Link to={`/app/billing/${inv.id}`} target="_blank" data-testid={`ipd-billing-view-${inv.id}`}>
                                <Button size="sm" variant="ghost" className="h-7 px-2 text-brand hover:bg-brand/5">
                                  <ExternalLink size={11} className="mr-1" />View
                                </Button>
                              </Link>
                              <a href={`/print/invoice/${inv.id}`} target="_blank" rel="noreferrer" data-testid={`ipd-billing-print-${inv.id}`}>
                                <Button size="sm" variant="ghost" className="h-7 px-2"><Printer size={11} className="mr-1" />Print</Button>
                              </a>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="discharge" className="mt-4">
          <Card className="p-5">
            <div className="mb-3 flex items-center justify-between">
              <div className="section-label">Discharge summary (NABH structured)</div>
              <div className="text-[10px] text-ink-muted">Click <b>Dictate</b> next to any field to speak instead of type.</div>
            </div>

            {/* Template selector — top */}
            <div className="mb-4 rounded-lg border border-violet-200 bg-violet-50/40 p-3" data-testid="disch-template-bar">
              <div className="flex flex-wrap items-end gap-2">
                <div className="flex-1 min-w-[200px]">
                  <Label className="text-[11px] uppercase tracking-wider text-violet-700 mb-1 block flex items-center gap-1.5">
                    <Sparkles size={12} />Apply a saved template
                  </Label>
                  <select
                    className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                    value={selectedTpl}
                    onChange={e => applyTemplate(e.target.value)}
                    data-testid="disch-template-select"
                  >
                    <option value="">— Select a template —</option>
                    {templates
                      .filter((t) => tplFilterCategory === "All" || (t.category || "Other") === tplFilterCategory)
                      .map(t => (
                        <option key={t.id} value={t.id}>
                          {t.name}{t.category ? ` · ${t.category}` : ""}{t.description ? ` · ${t.description}` : ""}
                        </option>
                      ))}
                  </select>
                </div>
                {selectedTpl && (
                  <Button
                    variant="outline" size="sm"
                    className="border-rose-200 text-rose-600 hover:bg-rose-50"
                    onClick={() => deleteTemplate(selectedTpl, templates.find(t => t.id === selectedTpl)?.name)}
                    data-testid="disch-template-delete"
                    title="Delete selected template"
                  >
                    <Trash2 size={14} />
                  </Button>
                )}
                <div className="text-[11px] text-ink-muted">
                  {templates.length === 0 ? "No templates yet — fill the form & click Save as template at the bottom." : `${templates.length} template${templates.length === 1 ? "" : "s"} available`}
                </div>
              </div>
              {templates.length > 0 && (
                <div className="mt-2.5 flex flex-wrap gap-1.5" data-testid="disch-template-category-filter">
                  {["All", ...SURGERY_CATEGORIES].map((cat) => {
                    const count = cat === "All" ? templates.length : templates.filter((t) => (t.category || "Other") === cat).length;
                    if (cat !== "All" && count === 0) return null;
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => setTplFilterCategory(cat)}
                        className={`rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition ${tplFilterCategory === cat ? "border-violet-600 bg-violet-600 text-white" : "border-violet-200 bg-white text-ink hover:border-violet-600"}`}
                        data-testid={`disch-tpl-cat-${cat.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                      >
                        {cat} <span className="opacity-70">· {count}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* IOL auto-bind banner (from latest OT note) */}
            {otBound && discharge.iol_used && (discharge.iol_used.implant_name || discharge.iol_used.model) && (
              <div className="mb-4 flex items-start gap-3 rounded-lg border-l-4 border-emerald-400 bg-emerald-50 px-3 py-2.5" data-testid="disch-iol-bound-banner">
                <Scissors size={16} className="mt-0.5 text-emerald-700" />
                <div className="flex-1 text-xs text-emerald-900">
                  <div className="font-heading text-sm font-semibold">IOL auto-bound from OT</div>
                  <div className="mt-0.5 grid grid-cols-2 gap-x-3 gap-y-0.5 md:grid-cols-4">
                    {discharge.iol_used.implant_name && <span><b>Implant:</b> {discharge.iol_used.implant_name}</span>}
                    {discharge.iol_used.model && <span><b>Model:</b> {discharge.iol_used.model}</span>}
                    {discharge.iol_used.power && <span><b>Power:</b> {discharge.iol_used.power}</span>}
                    {discharge.iol_used.batch_no && <span><b>Batch:</b> <span className="font-mono">{discharge.iol_used.batch_no}</span></span>}
                    {discharge.iol_used.lot_no && <span><b>Lot:</b> <span className="font-mono">{discharge.iol_used.lot_no}</span></span>}
                    {discharge.iol_used.expiry && <span><b>Expiry:</b> {discharge.iol_used.expiry}</span>}
                  </div>
                </div>
              </div>
            )}

            <div className="grid gap-4">
              {DISCHARGE_FIELDS.filter(f => !f.afterRx).map(f => (
                <div key={f.key}>
                  <div className="flex items-center justify-between">
                    <Label>{f.label}</Label>
                    <MicDictateButton
                      testId={`mic-disch-${f.key}`}
                      value={discharge[f.key] || ""}
                      onChange={(v) => setDischarge({ ...discharge, [f.key]: v })}
                      compact
                    />
                  </div>
                  {f.area
                    ? <Textarea rows={2} value={discharge[f.key] || ""} onChange={e => setDischarge({...discharge, [f.key]: e.target.value})} data-testid={`disch-${f.key}`} />
                    : <Input value={discharge[f.key] || ""} onChange={e => setDischarge({...discharge, [f.key]: e.target.value})} data-testid={`disch-${f.key}`} />
                  }
                </div>
              ))}

              {/* Structured Discharge prescription — table mirrors OPD Rx */}
              <div>
                <div className="mb-2 flex items-center justify-between">
                  <Label className="flex items-center gap-1.5"><Pill size={14} className="text-brand" />Discharge medication</Label>
                  <Button size="sm" variant="outline" onClick={addRx} data-testid="disch-add-rx"><Plus size={12} className="mr-1" />Add medicine</Button>
                </div>
                {rxRows.length === 0
                  ? <div className="rounded-md border border-dashed border-border bg-secondary/30 px-3 py-4 text-xs text-ink-muted">No medicines added. Click <b>Add medicine</b> to start, or apply a template above.</div>
                  : (
                    <div className="overflow-x-auto rounded-md border border-border" data-testid="disch-rx-table">
                      <datalist id="disch-medicine-master">
                        {medicines.map((m) => <option key={m.id} value={m.name}>{m.generic ? `${m.generic}` : ""}</option>)}
                      </datalist>
                      <datalist id="disch-frequency-master">
                        {["1-0-0", "0-0-1", "1-0-1", "1-1-1", "1-1-1-1", "BD", "TID", "QID", "Q4H", "Q6H", "Q8H", "Q2H", "SOS", "HS", "STAT"].map((x) => <option key={x} value={x} />)}
                      </datalist>
                      <datalist id="disch-timing-master">
                        {["Before food", "After food", "With food", "Empty stomach", "At bedtime", "On waking", "Anytime"].map((x) => <option key={x} value={x} />)}
                      </datalist>
                      <table className="w-full text-xs">
                        <thead className="bg-secondary/60 text-left text-[10px] uppercase tracking-wider text-ink-muted">
                          <tr>
                            <th className="px-2 py-2 w-8">#</th>
                            <th className="px-2 py-2 min-w-[180px]">Medicine</th>
                            <th className="px-2 py-2">Strength</th>
                            <th className="px-2 py-2">Dose</th>
                            <th className="px-2 py-2">Route</th>
                            <th className="px-2 py-2">Eye</th>
                            <th className="px-2 py-2">Frequency</th>
                            <th className="px-2 py-2">Timing</th>
                            <th className="px-2 py-2">Duration</th>
                            <th className="px-2 py-2">Instructions</th>
                            <th className="px-2 py-2"></th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {rxRows.map((m, i) => {
                            const stock = stockOf(m);
                            return (
                              <tr key={i}>
                                <td className="px-2 py-1 text-ink-muted">{i + 1}</td>
                                <td className="px-2 py-1">
                                  <Input list="disch-medicine-master" placeholder="Type to search master…" value={m.medicine || ""} onChange={e => updateRx(i, "medicine", e.target.value)} className="h-8" data-testid={`disch-rx-medicine-${i}`} />
                                  {m.generic_name && <div className="mt-0.5 text-[10px] text-ink-muted" data-testid={`disch-rx-generic-${i}`}>{m.generic_name}</div>}
                                  {stock !== null && (
                                    <div className={`mt-0.5 text-[10px] font-semibold ${stock > 10 ? "text-emerald-600" : stock > 0 ? "text-amber-600" : "text-rose-600"}`} data-testid={`disch-rx-stock-${i}`}>
                                      Stock: {stock}
                                    </div>
                                  )}
                                </td>
                                <td className="px-2 py-1"><Input placeholder="0.5%" value={m.strength || ""} onChange={e => updateRx(i, "strength", e.target.value)} className="h-8 w-20" /></td>
                                <td className="px-2 py-1"><Input placeholder="1 drop" value={m.dosage || ""} onChange={e => updateRx(i, "dosage", e.target.value)} className="h-8 w-20" /></td>
                                <td className="px-2 py-1">
                                  <Select value={m.route || "Topical"} onValueChange={v => updateRx(i, "route", v)}>
                                    <SelectTrigger className="h-8 w-24"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      {["Topical", "Oral", "IV", "IM", "SC", "Subconjunctival", "Intravitreal"].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                </td>
                                <td className="px-2 py-1">
                                  <Select value={m.eye || "OU"} onValueChange={v => updateRx(i, "eye", v)}>
                                    <SelectTrigger className="h-8 w-16"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                      {["OD", "OS", "OU", "NA", "_none"].map(e => <SelectItem key={e} value={e}>{e === "_none" ? "—" : e}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                </td>
                                <td className="px-2 py-1"><Input list="disch-frequency-master" placeholder="QID / 1-0-1" value={m.frequency || ""} onChange={e => updateRx(i, "frequency", e.target.value)} className="h-8 w-24" data-testid={`disch-rx-frequency-${i}`} /></td>
                                <td className="px-2 py-1"><Input list="disch-timing-master" placeholder="After meals" value={m.timing || ""} onChange={e => updateRx(i, "timing", e.target.value)} className="h-8 w-28" /></td>
                                <td className="px-2 py-1"><Input placeholder="2 weeks" value={m.duration || ""} onChange={e => updateRx(i, "duration", e.target.value)} className="h-8 w-20" data-testid={`disch-rx-duration-${i}`} /></td>
                                <td className="px-2 py-1"><Input placeholder="Taper as advised" value={m.instructions || ""} onChange={e => updateRx(i, "instructions", e.target.value)} className="h-8 min-w-[120px]" /></td>
                                <td className="px-2 py-1"><Button variant="ghost" size="icon" onClick={() => removeRx(i)} data-testid={`disch-rx-delete-${i}`}><Trash2 size={14} className="text-rose-500" /></Button></td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                <div className="mt-1 text-[10px] text-ink-muted">Linked to medicine master · generic name & stock auto-shown when a master row matches. Stock counts do not appear on the printed summary.</div>
              </div>

              {DISCHARGE_FIELDS.filter(f => f.afterRx).map(f => (
                <div key={f.key}>
                  <div className="flex items-center justify-between">
                    <Label>{f.label}</Label>
                    <MicDictateButton
                      testId={`mic-disch-${f.key}`}
                      value={discharge[f.key] || ""}
                      onChange={(v) => setDischarge({ ...discharge, [f.key]: v })}
                      compact
                    />
                  </div>
                  {f.area
                    ? <Textarea rows={2} value={discharge[f.key] || ""} onChange={e => setDischarge({...discharge, [f.key]: e.target.value})} data-testid={`disch-${f.key}`} />
                    : <Input value={discharge[f.key] || ""} onChange={e => setDischarge({...discharge, [f.key]: e.target.value})} data-testid={`disch-${f.key}`} />
                  }
                </div>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
              <Button
                variant="outline"
                className="border-violet-300 text-violet-700 hover:bg-violet-50"
                onClick={() => setTplDialogOpen(true)}
                data-testid="disch-save-as-template"
              >
                <BookmarkPlus size={14} className="mr-2" />Save as template
              </Button>
              <div className="flex gap-2">
                {data.discharge && <a href={`/print/discharge/${data.discharge.id}`} target="_blank" rel="noreferrer"><Button variant="outline">Print summary</Button></a>}
                <Button onClick={saveDischarge} className="bg-brand hover:bg-brand-700" data-testid="disch-save-btn"><Save size={14} className="mr-2" />Save & discharge</Button>
              </div>
            </div>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Save-as-template dialog */}
      <Dialog open={tplDialogOpen} onOpenChange={setTplDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Save discharge summary as template</DialogTitle></DialogHeader>
          <p className="text-sm text-ink-muted">Templates make repeat procedures faster. The IOL details and patient-specific data are not saved — only the standard NABH narrative fields.</p>
          <div className="mt-3 grid gap-3">
            <div>
              <Label>Template name *</Label>
              <Input value={tplName} onChange={e => setTplName(e.target.value)} placeholder="e.g. Routine Phaco-IOL discharge" data-testid="disch-tpl-name" />
            </div>
            <div>
              <Label>Surgery / diagnosis category</Label>
              <select
                className="mt-1 w-full rounded-md border bg-background px-3 py-2 text-sm"
                value={tplCategory}
                onChange={(e) => setTplCategory(e.target.value)}
                data-testid="disch-tpl-category"
              >
                <option value="">— Uncategorised —</option>
                {SURGERY_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <div className="mt-1 text-[10px] text-ink-muted">Groups templates by surgery / diagnosis so they're easier to find.</div>
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input value={tplDesc} onChange={e => setTplDesc(e.target.value)} placeholder="Short note for fellow doctors" data-testid="disch-tpl-desc" />
            </div>
          </div>
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setTplDialogOpen(false)}>Cancel</Button>
            <Button onClick={saveAsTemplate} className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="disch-tpl-save-btn">
              <BookmarkPlus size={14} className="mr-2" />Save template
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
